import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  Activity, Plus, RefreshCw, Radio, ZoomOut, Maximize2, Minimize2,
  Loader2, Trash2, Search, ArrowDownToLine, ArrowUpFromLine, Gauge,
  Pencil, X, ChevronRight, ServerCog, Wifi,
} from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import ZabbixHostAutocomplete from '@/components/ZabbixHostAutocomplete';

const RANGES = [
  { key: '5m', label: '5m' }, { key: '15m', label: '15m' }, { key: '1h', label: '1h' },
  { key: '6h', label: '6h' }, { key: '12h', label: '12h' }, { key: '24h', label: '24h' },
  { key: '7d', label: '7d' }, { key: '30d', label: '30d' },
];

const IN_COLOR = '#34d399';   // emerald — IN
const OUT_COLOR = '#38bdf8';  // sky — OUT

// ---- formatters ------------------------------------------------------------
function fmtBps(v) {
  if (v == null || isNaN(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(2)} Gbps`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(2)} Mbps`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(2)} Kbps`;
  return `${v.toFixed(0)} bps`;
}
function fmtAxisBps(v) {
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)}G`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return `${v.toFixed(0)}`;
}
function fmtClock(ms) {
  const d = new Date(ms); const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}
function fmtFull(ms) {
  const d = new Date(ms); const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
function fmtDay(ms) {
  const d = new Date(ms); const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// ---------------------------------------------------------------------------
// Item picker — searchable dropdown of Zabbix items for a host
// ---------------------------------------------------------------------------
function ItemPicker({ hostid, value, onChange, direction, testId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!hostid) { setItems([]); return; }
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/zabbix/items', { params: { hostid } });
        if (!ignore) setItems(data.items || []);
      } catch { if (!ignore) setItems([]); } finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, [hostid]);

  useEffect(() => {
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Rank traffic items to the top; keyword hint by direction
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const dirHint = direction === 'in'
      ? ['ifhcinoctets', 'ifinoctets', 'bits received', 'traffic in', 'received', 'inbound', 'rx']
      : ['ifhcoutoctets', 'ifoutoctets', 'bits sent', 'traffic out', 'sent', 'outbound', 'tx'];
    const score = (it) => {
      const hay = `${it.name || ''} ${it.key_ || ''}`.toLowerCase();
      let s = 0;
      if (dirHint.some((h) => hay.includes(h))) s += 100;
      if (hay.includes('octets') || hay.includes('bits') || hay.includes('traffic')) s += 20;
      if (hay.includes('discard') || hay.includes('error')) s -= 200;
      return s;
    };
    let list = items.filter((it) => {
      if (!q) return true;
      const hay = `${it.name || ''} ${it.key_ || ''}`.toLowerCase();
      return hay.includes(q);
    });
    list = [...list].sort((a, b) => score(b) - score(a));
    return list.slice(0, 80);
  }, [items, query, direction]);

  const pick = (it) => {
    onChange({
      itemid: String(it.itemid),
      name: it.name || '',
      key: it.key_ || '',
      units: it.units || '',
      value_type: Number(it.value_type ?? 3),
    });
    setOpen(false);
  };

  const Icon = direction === 'in' ? ArrowDownToLine : ArrowUpFromLine;
  const color = direction === 'in' ? 'text-emerald-400' : 'text-sky-400';

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        disabled={!hostid}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-left text-[13px] transition-colors',
          !hostid ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50',
        )}
        data-testid={`${testId}-trigger`}
      >
        <Icon className={cn('w-4 h-4 shrink-0', color)} />
        <span className="flex-1 min-w-0 truncate">
          {value?.itemid
            ? <span className="text-foreground">{value.name || value.key}</span>
            : <span className="text-muted-foreground">{hostid ? `Pilih item ${direction === 'in' ? 'IN' : 'OUT'}…` : 'Pilih host dulu'}</span>}
        </span>
        <ChevronRight className={cn('w-4 h-4 text-muted-foreground transition-transform', open && 'rotate-90')} />
      </button>

      {open && hostid && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-xl">
          <div className="p-2 border-b border-border/60">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cari item / key…"
                className="pl-7 h-8 text-xs font-mono"
                data-testid={`${testId}-search`}
              />
            </div>
          </div>
          <ScrollArea className="max-h-[240px]">
            <div className="p-1">
              {loading ? (
                <div className="flex items-center gap-2 px-3 py-3 text-xs text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Memuat item dari Zabbix…
                </div>
              ) : filtered.length === 0 ? (
                <div className="px-3 py-3 text-xs text-muted-foreground">Tidak ada item.</div>
              ) : filtered.map((it) => (
                <button
                  key={it.itemid}
                  type="button"
                  onClick={() => pick(it)}
                  className={cn(
                    'w-full text-left px-2.5 py-1.5 rounded text-xs hover:bg-accent flex flex-col gap-0.5',
                    String(value?.itemid) === String(it.itemid) && 'bg-primary/10',
                  )}
                  data-testid={`${testId}-option-${it.itemid}`}
                >
                  <span className="truncate text-foreground">{it.name}</span>
                  <span className="flex items-center gap-2 text-[10px] text-muted-foreground font-mono">
                    <span className="truncate">{it.key_}</span>
                    {it.units && <span className="px-1 rounded bg-slate-500/10 shrink-0">{it.units}</span>}
                    {it.lastvalue != null && it.lastvalue !== '' && (
                      <span className="shrink-0">last: {it.lastvalue}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add / Edit MRTG dialog
// ---------------------------------------------------------------------------
function MRTGFormDialog({ open, onOpenChange, initial, onSaved }) {
  const { toast } = useToast();
  const editing = !!initial?.id;
  const [name, setName] = useState('');
  const [hostName, setHostName] = useState('');
  const [hostid, setHostid] = useState('');
  const [iface, setIface] = useState('');
  const [inItem, setInItem] = useState(null);
  const [outItem, setOutItem] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initial?.name || '');
      setHostName(initial?.host_name || '');
      setHostid(initial?.hostid || '');
      setIface(initial?.interface || '');
      setInItem(initial?.in_item || null);
      setOutItem(initial?.out_item || null);
    }
  }, [open, initial]);

  const submit = async () => {
    if (!name.trim()) return toast({ title: 'Nama MRTG wajib diisi', variant: 'destructive' });
    if (!hostid) return toast({ title: 'Pilih host Zabbix', variant: 'destructive' });
    if (!inItem?.itemid || !outItem?.itemid) return toast({ title: 'Pilih item IN dan OUT', variant: 'destructive' });
    setSaving(true);
    try {
      const payload = {
        name: name.trim(), hostid, host_name: hostName, interface: iface.trim(),
        in_item: inItem, out_item: outItem,
      };
      const res = editing
        ? await api.put(`/mrtg-traffic/configs/${initial.id}`, payload)
        : await api.post('/mrtg-traffic/configs', payload);
      toast({ title: editing ? 'MRTG diperbarui' : 'MRTG ditambahkan' });
      onSaved(res.data);
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Gagal menyimpan', description: formatApiError(e), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid="mrtg-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-primary" />
            {editing ? 'Edit MRTG Traffic' : 'Add MRTG Traffic'}
          </DialogTitle>
          <DialogDescription>
            Konfigurasi graph traffic dari host & item Zabbix yang sudah ada.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs">MRTG Name</Label>
            <Input
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="cth: Uplink Core ke Upstream A"
              data-testid="mrtg-name-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5"><ServerCog className="w-3.5 h-3.5" /> Host Name (Zabbix)</Label>
            <ZabbixHostAutocomplete
              value={hostName}
              onChange={(n) => { setHostName(n); if (!n) { setHostid(''); } }}
              onSelectHost={(h) => {
                if (h) { setHostid(h.hostid); setHostName(h.name); setInItem(null); setOutItem(null); }
                else { setHostid(''); }
              }}
              testId="mrtg-host"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5"><Wifi className="w-3.5 h-3.5" /> Device / Interface <span className="text-muted-foreground font-normal">(opsional)</span></Label>
            <Input
              value={iface} onChange={(e) => setIface(e.target.value)}
              placeholder="cth: ether1 / 10GE1/0/5"
              className="font-mono text-[13px]"
              data-testid="mrtg-iface-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-emerald-400 flex items-center gap-1.5"><ArrowDownToLine className="w-3.5 h-3.5" /> IN Traffic Item</Label>
              <ItemPicker hostid={hostid} value={inItem} onChange={setInItem} direction="in" testId="mrtg-in-item" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-sky-400 flex items-center gap-1.5"><ArrowUpFromLine className="w-3.5 h-3.5" /> OUT Traffic Item</Label>
              <ItemPicker hostid={hostid} value={outItem} onChange={setOutItem} direction="out" testId="mrtg-out-item" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="mrtg-form-cancel">Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="mrtg-form-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Simpan Perubahan' : 'Tambah MRTG')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Stat pill
// ---------------------------------------------------------------------------
function Stat({ label, value, color }) {
  return (
    <div className="flex flex-col rounded-lg border border-border/70 bg-background/40 px-3 py-2 min-w-[110px]">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold tabular-nums" style={{ color }}>{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Traffic chart panel
// ---------------------------------------------------------------------------
function TrafficPanel({ config }) {
  const { toast } = useToast();
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [range, setRange] = useState('1h');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [live, setLive] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!config?.id) return;
    if (!silent) setLoading(true);
    try {
      const { data: res } = await api.get(`/mrtg-traffic/configs/${config.id}/series`, { params: { range } });
      setData(res);
    } catch (e) {
      if (!silent) toast({ title: 'Gagal memuat graph', description: formatApiError(e), variant: 'destructive' });
    } finally { if (!silent) setLoading(false); }
  }, [config?.id, range, toast]);

  useEffect(() => { setData(null); load(); }, [load]);

  useEffect(() => {
    if (!live) return undefined;
    const small = ['5m', '15m', '1h'].includes(range);
    const id = setInterval(() => load(true), small ? 10000 : 30000);
    return () => clearInterval(id);
  }, [live, range, load]);

  const option = useMemo(() => {
    const inPts = data?.in?.points || [];
    const outPts = data?.out?.points || [];
    const multiDay = ['24h', '7d', '30d'].includes(range);
    const fmtTick = (val) => (multiDay ? fmtDay(val) : fmtClock(val));
    return {
      backgroundColor: 'transparent',
      animation: false,
      grid: { left: 62, right: 20, top: 24, bottom: 74 },
      textStyle: { color: '#94a3b8' },
      legend: {
        top: 0, left: 0,
        textStyle: { color: '#cbd5e1', fontSize: 12 },
        inactiveColor: '#475569',
        icon: 'roundRect',
        data: ['IN', 'OUT'],
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(15,23,42,0.96)',
        borderColor: '#334155',
        textStyle: { color: '#e2e8f0', fontSize: 12 },
        axisPointer: { type: 'cross', crossStyle: { color: '#64748b' }, lineStyle: { color: '#64748b' } },
        formatter: (params) => {
          if (!params || !params.length) return '';
          const ts = params[0].axisValue;
          let html = `<div style="font-weight:600;margin-bottom:4px">${fmtFull(ts)}</div>`;
          params.forEach((p) => {
            const val = p.data && p.data[1] != null ? fmtBps(Number(p.data[1])) : '—';
            html += `<div style="display:flex;align-items:center;gap:6px">
              <span style="display:inline-block;width:9px;height:9px;border-radius:2px;background:${p.color}"></span>
              <span style="flex:1">${p.seriesName}</span>
              <span style="font-weight:600;margin-left:12px">${val}</span></div>`;
          });
          return html;
        },
      },
      xAxis: {
        type: 'time',
        axisLine: { lineStyle: { color: '#334155' } },
        axisLabel: { color: '#94a3b8', fontSize: 11, hideOverlap: true, formatter: (val) => fmtTick(val) },
        splitLine: { show: false },
        axisPointer: { label: { formatter: (p) => fmtFull(p.value) } },
      },
      yAxis: {
        type: 'value',
        name: 'bit/s',
        nameTextStyle: { color: '#64748b', fontSize: 10 },
        axisLabel: { color: '#94a3b8', fontSize: 11, formatter: (v) => fmtAxisBps(v) },
        splitLine: { lineStyle: { color: 'rgba(51,65,85,0.4)' } },
        min: 0,
      },
      dataZoom: [
        { type: 'inside', filterMode: 'none', zoomOnMouseWheel: true, moveOnMouseMove: true, moveOnMouseWheel: false },
        { type: 'slider', height: 24, bottom: 18, backgroundColor: 'rgba(30,41,59,0.5)',
          fillerColor: 'rgba(56,189,248,0.15)', borderColor: '#334155',
          textStyle: { color: '#94a3b8', fontSize: 10 }, labelFormatter: (val) => fmtTick(val) },
      ],
      series: [
        {
          name: 'IN', type: 'line', showSymbol: false, sampling: 'lttb', smooth: 0.15,
          lineStyle: { width: 1.6, color: IN_COLOR }, itemStyle: { color: IN_COLOR },
          areaStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(52,211,153,0.35)' },
                { offset: 1, color: 'rgba(52,211,153,0.02)' },
              ],
            },
          },
          emphasis: { focus: 'series' },
          data: inPts,
        },
        {
          name: 'OUT', type: 'line', showSymbol: false, sampling: 'lttb', smooth: 0.15,
          lineStyle: { width: 1.6, color: OUT_COLOR }, itemStyle: { color: OUT_COLOR },
          areaStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(56,189,248,0.22)' },
                { offset: 1, color: 'rgba(56,189,248,0.01)' },
              ],
            },
          },
          emphasis: { focus: 'series' },
          data: outPts,
        },
      ],
    };
  }, [data, range]);

  const resetZoom = () => {
    chartRef.current?.getEchartsInstance()?.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
  };
  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!document.fullscreenElement) el?.requestFullscreen?.().then(() => setFullscreen(true)).catch(() => setFullscreen(true));
    else document.exitFullscreen?.().then(() => setFullscreen(false)).catch(() => setFullscreen(false));
  };
  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const hasData = (data?.in?.points?.length || 0) + (data?.out?.points?.length || 0) > 0;

  return (
    <div ref={containerRef} className="rounded-xl border border-border bg-card flex flex-col" data-testid="mrtg-graph-panel">
      {/* Panel header */}
      <div className="flex flex-wrap items-start justify-between gap-3 p-4 border-b border-border">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <h2 className="text-base font-semibold truncate">{config.name}</h2>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 font-mono truncate">
            {config.host_name}{config.interface ? ` · ${config.interface}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Stat label="IN now" value={fmtBps(data?.in?.last)} color={IN_COLOR} />
          <Stat label="IN avg" value={fmtBps(data?.in?.avg)} color={IN_COLOR} />
          <Stat label="IN max" value={fmtBps(data?.in?.max)} color={IN_COLOR} />
          <Stat label="OUT now" value={fmtBps(data?.out?.last)} color={OUT_COLOR} />
          <Stat label="OUT avg" value={fmtBps(data?.out?.avg)} color={OUT_COLOR} />
          <Stat label="OUT max" value={fmtBps(data?.out?.max)} color={OUT_COLOR} />
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-border bg-background/30">
        <div className="flex flex-wrap items-center gap-1" data-testid="mrtg-range-selector">
          {RANGES.map((rg) => (
            <button
              key={rg.key}
              onClick={() => setRange(rg.key)}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                range === rg.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-accent',
              )}
              data-testid={`mrtg-range-${rg.key}`}
            >
              {rg.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          {data?.source && (
            <Badge variant="outline" className="text-[10px] font-normal">
              {data.source === 'trend' ? 'trend (rollup)' : 'history'}
            </Badge>
          )}
          <Button variant={live ? 'default' : 'outline'} size="sm" onClick={() => setLive((v) => !v)} title="Auto-refresh" data-testid="mrtg-live-toggle">
            <Radio className={cn('w-4 h-4', live && 'animate-pulse')} />
            <span className="ml-1.5 hidden sm:inline">{live ? 'Live' : 'Live off'}</span>
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => load()} title="Refresh" data-testid="mrtg-refresh-btn">
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={resetZoom} title="Reset zoom">
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleFullscreen} title="Fullscreen">
            {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Chart */}
      <div className={cn('relative p-2', fullscreen && 'bg-background flex-1')}>
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/60 backdrop-blur-sm">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}
        {!loading && !hasData ? (
          <div className="flex flex-col items-center justify-center h-[420px] text-muted-foreground text-center px-6">
            <Activity className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm font-medium text-foreground">Belum ada data untuk range ini</p>
            <p className="text-xs mt-1 max-w-sm">Zabbix belum punya history pada rentang <b>{range}</b> untuk item ini, atau item baru saja dibuat. Coba range lebih besar.</p>
          </div>
        ) : (
          <ReactECharts
            ref={chartRef}
            option={option}
            notMerge={false}
            lazyUpdate
            style={{ height: fullscreen ? 'calc(100vh - 210px)' : '440px', width: '100%' }}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function MRTGTraffic() {
  const { toast } = useToast();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [search, setSearch] = useState('');

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/mrtg-traffic/configs');
      const items = data.items || [];
      setConfigs(items);
      setSelectedId((cur) => cur && items.some((c) => c.id === cur) ? cur : (items[0]?.id || null));
    } catch (e) {
      toast({ title: 'Gagal memuat daftar MRTG', description: formatApiError(e), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const onSaved = (saved) => {
    loadConfigs();
    if (saved?.id) setSelectedId(saved.id);
  };

  const remove = async (cfg, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Hapus MRTG "${cfg.name}"?`)) return;
    try {
      await api.delete(`/mrtg-traffic/configs/${cfg.id}`);
      toast({ title: 'MRTG dihapus' });
      loadConfigs();
    } catch (err) {
      toast({ title: 'Gagal menghapus', description: formatApiError(err), variant: 'destructive' });
    }
  };

  const openEdit = (cfg, e) => { e?.stopPropagation(); setEditTarget(cfg); setDialogOpen(true); };
  const openAdd = () => { setEditTarget(null); setDialogOpen(true); };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return configs;
    return configs.filter((c) => `${c.name} ${c.host_name} ${c.interface}`.toLowerCase().includes(q));
  }, [configs, search]);

  const selected = configs.find((c) => c.id === selectedId) || null;

  return (
    <div className="space-y-4" data-testid="mrtg-traffic-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Gauge className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-display font-bold tracking-tight">MRTG Traffic</h1>
            <p className="text-sm text-muted-foreground">
              Interface traffic monitoring · Zabbix ·{' '}
              <span className="text-primary font-medium">{configs.length}</span> graph
            </p>
          </div>
        </div>
        <Button onClick={openAdd} data-testid="mrtg-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add MRTG
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : configs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card/50 py-20 flex flex-col items-center text-center px-6" data-testid="mrtg-empty-state">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
            <Gauge className="w-7 h-7 text-primary" />
          </div>
          <h3 className="text-base font-semibold">Belum ada MRTG Traffic</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md">
            Buat graph traffic pertama Anda. Pilih host & item IN/OUT dari Zabbix, lalu pantau bandwidth secara real-time ala Grafana.
          </p>
          <Button onClick={openAdd} className="mt-5" data-testid="mrtg-empty-add-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add MRTG
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* List */}
          <div className="lg:col-span-1 rounded-xl border border-border bg-card overflow-hidden flex flex-col">
            <div className="p-3 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Cari MRTG…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9" data-testid="mrtg-list-search" />
              </div>
            </div>
            <ScrollArea className="flex-1 max-h-[600px]">
              <div className="p-2 space-y-1.5" data-testid="mrtg-list">
                {filtered.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    className={cn(
                      'group cursor-pointer rounded-lg border px-3 py-2.5 transition-colors',
                      selectedId === c.id ? 'border-primary/60 bg-primary/10' : 'border-border/60 hover:border-border hover:bg-accent/50',
                    )}
                    data-testid={`mrtg-list-item-${c.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{c.name}</p>
                        <p className="text-[11px] text-muted-foreground font-mono truncate mt-0.5">
                          {c.host_name}{c.interface ? ` · ${c.interface}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => openEdit(c, e)} className="p-1 rounded hover:bg-background text-muted-foreground hover:text-foreground" title="Edit" data-testid={`mrtg-edit-${c.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={(e) => remove(c, e)} className="p-1 rounded hover:bg-background text-muted-foreground hover:text-rose-400" title="Hapus" data-testid={`mrtg-delete-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Graph */}
          <div className="lg:col-span-3">
            {selected ? (
              <TrafficPanel key={selected.id} config={selected} />
            ) : (
              <div className="rounded-xl border border-border bg-card h-[520px] flex items-center justify-center text-muted-foreground">
                Pilih satu MRTG dari daftar.
              </div>
            )}
          </div>
        </div>
      )}

      <MRTGFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editTarget}
        onSaved={onSaved}
      />
    </div>
  );
}
